import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AttachMoney as PriceIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { apiCache } from '@/services';
import type { CacheStatus as CacheStatusType, CacheRefreshProgress } from '@/types';

interface PriceCacheStatus {
  count: number;
  lastUpdated: number | null;
  isStale: boolean;
}

export const CacheStatus: React.FC = () => {
  const [status, setStatus] = useState<CacheStatusType | null>(null);
  const [priceStatus, setPriceStatus] = useState<PriceCacheStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState<CacheRefreshProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [priceMessage, setPriceMessage] = useState<string | null>(null);

  const loadStatus = async () => {
    try {
      const [cacheStatus, priceCacheStatus] = await Promise.all([
        apiCache.getStatus(),
        apiCache.getPriceCacheStatus(),
      ]);
      setStatus(cacheStatus);
      setPriceStatus(priceCacheStatus);
      setError(null);
    } catch (err) {
      setError('Failed to load cache status');
      console.error(err);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    setRefreshProgress({ step: 'Starting refresh', progress: 0 });

    try {
      await apiCache.refreshAll((progress) => {
        setRefreshProgress(progress);
      });

      await loadStatus();
      setRefreshProgress(null);
    } catch (err) {
      setError('Failed to refresh cache');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPrices = async () => {
    setPriceLoading(true);
    setError(null);
    setPriceMessage(null);

    try {
      const hadPrices = priceStatus && priceStatus.count > 0;
      await apiCache.refreshPrices();
      await loadStatus();

      if (hadPrices) {
        // Dispatch event to notify Calculator to re-fetch prices
        window.dispatchEvent(new CustomEvent('pricesRefreshed'));
        setPriceMessage('Price cache cleared. Prices will be re-fetched.');
      } else {
        setPriceMessage('No prices were cached. Prices are fetched when you calculate a BOM.');
      }

      // Clear message after 5 seconds
      setTimeout(() => setPriceMessage(null), 5000);
    } catch (err) {
      setError('Failed to refresh prices');
      console.error(err);
    } finally {
      setPriceLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (!status) return null;

    if (!status.isPopulated) {
      return <ErrorIcon color="error" />;
    }
    if (!status.isValid) {
      return <ErrorIcon color="error" />;
    }
    if (status.isStale) {
      return <WarningIcon color="warning" />;
    }
    return <CheckCircleIcon color="success" />;
  };

  const getStatusText = () => {
    if (!status) return 'Loading...';

    if (!status.isPopulated) {
      return 'Cache empty - click Refresh to load data';
    }
    if (!status.isValid) {
      return 'Cache invalid - refresh required';
    }
    if (status.isStale) {
      return 'Cache is stale - consider refreshing';
    }
    return 'Cache is up to date';
  };

  const getStatusColor = () => {
    if (!status) return 'default';
    if (!status.isPopulated || !status.isValid) return 'error';
    if (status.isStale) return 'warning';
    return 'success';
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const formatLastUpdated = () => {
    if (!status?.lastUpdated) return 'Never';
    return formatTimestamp(status.lastUpdated);
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              {getStatusIcon()}
              <Typography variant="h6">Cache Status</Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading || priceLoading}
              >
                Refresh Data
              </Button>
              <Button
                variant="outlined"
                startIcon={<PriceIcon />}
                onClick={handleRefreshPrices}
                disabled={loading || priceLoading}
              >
                Refresh Prices
              </Button>
            </Stack>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          {priceMessage && (
            <Alert severity="info" onClose={() => setPriceMessage(null)}>
              {priceMessage}
            </Alert>
          )}

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            <Chip label={getStatusText()} color={getStatusColor()} size="small" />
          </Box>

          {status && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="body1">{formatLastUpdated()}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Cached Items
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Chip
                    label={`${status.itemCounts.categories} Categories`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${status.itemCounts.items} Items`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${status.itemCounts.itemBlueprints} Blueprints`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${status.itemCounts.industrySkills} Skills`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Box>

              {priceStatus && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Price Cache
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${priceStatus.count} Prices Cached`}
                      size="small"
                      variant="outlined"
                      color={priceStatus.isStale ? 'warning' : 'default'}
                    />
                    {priceStatus.isStale && priceStatus.count > 0 && (
                      <Chip icon={<WarningIcon />} label="Stale" size="small" color="warning" />
                    )}
                    {priceStatus.lastUpdated && (
                      <Typography variant="caption" color="text.secondary">
                        Updated {formatTimestamp(priceStatus.lastUpdated)}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </>
          )}

          {refreshProgress && (
            <Box>
              <Typography variant="body2" gutterBottom>
                {refreshProgress.step}
              </Typography>
              <LinearProgress variant="determinate" value={refreshProgress.progress} />
              <Typography variant="caption" color="text.secondary">
                {Math.round(refreshProgress.progress)}%
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
